import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';

/** Eigene Mitgliedschaft in einem aktiven Team. */
export interface ResourceTeamAccess {
  teamId: number;
  teamName: string;
  isLeader: boolean;
  isPrimaryTeam: boolean;
}

/** Team, das geplant bzw. dessen Katalog verwaltet werden darf. */
export interface ResourceTeamOption {
  teamId: number;
  teamName: string;
}

/**
 * Rechte des angemeldeten Benutzers (nur zur Steuerung der Oberfläche – jeder
 * API-Endpunkt prüft die Berechtigung selbst).
 */
export interface ResourcePlanningAccess {
  userId: string;
  isSuperAdmin: boolean;
  isGlobalPlanner: boolean;
  canManagePlanning: boolean;
  canViewCapacity: boolean;
  canManageSkills: boolean;
  canManageCategories: boolean;
  canManageGlobalCatalog: boolean;
  canManageEmployees: boolean;
  canCreateTeam: boolean;
  /** Team- & Mitarbeiterverwaltung: global, Teamleitung oder Recht zur Teamanlage. */
  canManageTeams: boolean;
  teams: ResourceTeamAccess[];
  /** Global: alle aktiven Teams; Teamleiter: geleitete Teams. */
  planningTeams: ResourceTeamOption[];
  catalogTeams: ResourceTeamOption[];
}

/** Reiter des Bereichs „Verwaltung“ mit dem jeweils nötigen Recht (Reihenfolge = Standard). */
export interface ResourceAdminTab {
  path: string;
  label: string;
  icon: string;
  flag: 'canManageTeams' | 'canManageSkills';
}

export const RESOURCE_ADMIN_TABS: ResourceAdminTab[] = [
  { path: 'team-mitarbeiter', label: 'Team & Mitarbeiter', icon: 'groups', flag: 'canManageTeams' },
  { path: 'skills-kategorien', label: 'Skills & Kategorien', icon: 'category', flag: 'canManageSkills' }
];

@Injectable({ providedIn: 'root' })
export class ResourceAccessService {
  constructor(private http: HttpClient) {}
  getMine(): Observable<ResourcePlanningAccess> {
    return this.http.get<ResourcePlanningAccess>(environment.apiBaseUrl + '/resourceplanning/access/me');
  }

  /**
   * Standard-Teamauswahl einer Ansicht – überall dieselbe Regel, nur beim
   * Initialisieren verwenden (eine manuelle Auswahl wird nie überschrieben):
   *
   * 1. eigenes Hauptteam (bei genau einem Team automatisch dieses),
   * 2. sonst ein eigenes, geleitetes Team,
   * 3. sonst ein anderes eigenes Team,
   * jeweils nur, wenn es in `allowedTeamIds` (für die Ansicht berechtigt) liegt.
   * 4. Ohne passendes eigenes Team: „Global“ (null), falls `allowGlobal`,
   *    sonst das erste berechtigte Team.
   *
   * @returns Team-ID, oder null = Global bzw. keine Auswahl möglich.
   */
  static defaultTeamId(
    access: ResourcePlanningAccess | null,
    allowedTeamIds: number[],
    allowGlobal: boolean
  ): number | null {
    const allowed = new Set(allowedTeamIds);
    // access.teams enthält nur aktive Teams: ein gelöschtes/deaktiviertes Hauptteam fällt hier heraus.
    const memberships = (access?.teams ?? []).filter(t => allowed.has(t.teamId));
    const all = access?.teams ?? [];
    const primary = all.length === 1 ? all[0] : all.find(t => t.isPrimaryTeam);

    const choice =
      (primary && allowed.has(primary.teamId) ? primary : undefined) ??
      memberships.find(t => t.isLeader) ??
      memberships[0];
    if (choice) {
      return choice.teamId;
    }
    if (allowGlobal) {
      return null;
    }
    return allowedTeamIds[0] ?? null;
  }
}
